import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import ax from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, Unlock, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

export const RoundLocksPanel = ({ venues, canManage }) => {
  const [venueId, setVenueId] = useState('');
  const [locks, setLocks] = useState([]);
  const [verify, setVerify] = useState(null);

  useEffect(() => { if (!venueId && venues.length) setVenueId(venues[0].venue_id); }, [venues, venueId]);

  const load = useCallback(async (vid) => {
    if (!vid) return;
    setVerify(null);
    try { const { data } = await ax.get(`${API}/trivia/round-locks`, { params: { venue: vid } }); setLocks(data.locks); }
    catch { setLocks([]); }
  }, []);
  useEffect(() => { load(venueId); }, [venueId, load]);

  const release = async (l) => {
    if (!window.confirm(`Release "${l.round_file_name}"? Hosts can use it again right away.`)) return;
    try { await ax.post(`${API}/trivia/round-locks/${l.item_id}/release`); toast.success('Round released'); load(venueId); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const runVerify = async () => {
    try { const { data } = await ax.get(`${API}/trivia/round-locks/verify`, { params: { venue: venueId } }); setVerify(data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Verify failed'); }
  };

  const active = locks.filter((l) => !l.released);
  return (
    <Card data-testid="round-locks-panel" className="glass-panel border-0">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Lock className="w-5 h-5 text-[#fbdd68]" />Round Locks
        </CardTitle>
        <CardDescription className="text-[#8892b0]">
          Rounds used in the last 180 days, shared across every PC serving the venue. Admins can release one early.
        </CardDescription>
        <div className="flex items-center gap-3 flex-wrap mt-2">
          <select data-testid="locks-venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)}
            className="bg-[#000e2a] border border-[rgba(251,221,104,0.2)] text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#fbdd68]">
            {venues.map((v) => <option key={v.venue_id} value={v.venue_id}>{v.venue_name}</option>)}
          </select>
          <Button data-testid="verify-locks-btn" onClick={runVerify} variant="outline" size="sm"
            className="border-[rgba(251,221,104,0.3)] text-[#fbdd68] hover:bg-[#fbdd68]/10 h-9">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Verify PCs
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {active.length === 0 && <p className="text-sm text-[#8892b0] italic" data-testid="locks-empty">No locked rounds for this venue.</p>}
        {active.map((l) => (
          <div key={l.item_id} data-testid={`lock-row-${l.item_id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
            <div className="min-w-0">
              <p className="text-sm text-white font-medium truncate">{l.round_file_name}</p>
              <p className="text-xs text-[#8892b0]">
                Used {fmtDate(l.used_at)} · expires {fmtDate(l.expires_at)}
                {l.presentation_name ? ` · ${l.presentation_name}` : ''}{l.used_by ? ` · ${l.used_by}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {l.round_type && <Badge className="bg-[#fbdd68]/10 text-[#fbdd68] border-[#fbdd68]/30 text-[10px]">{l.round_type}</Badge>}
              {canManage && (
                <Button data-testid={`release-lock-${l.item_id}`} onClick={() => release(l)} variant="ghost" size="sm"
                  className="text-[#22c55e] hover:text-[#22c55e] hover:bg-[#22c55e]/10 h-8 px-2 text-xs">
                  <Unlock className="w-3.5 h-3.5 mr-1" />Release
                </Button>
              )}
            </div>
          </div>
        ))}
        {verify && (
          <div data-testid="verify-results" className="mt-3 p-3 rounded-lg bg-[#000e2a] border border-[rgba(251,221,104,0.15)] text-xs space-y-1.5">
            <p className="text-[#fbdd68] font-bold">{verify.active_locks} active lock(s) · {verify.programs.length} PC(s) serve this venue</p>
            {verify.programs.map((p) => (
              <p key={p.program_id} className="text-[#8892b0]">
                <span className="text-white">{p.name || p.program_id.slice(0, 8)}</span> — last seen {p.last_seen ? new Date(p.last_seen).toLocaleString() : 'never'} ·{' '}
                {p.likely_missing.length === 0 ? 'up to date' : `${p.likely_missing.length} lock(s) it likely lacks`}
              </p>
            ))}
            <p className="text-[#8892b0] italic">{verify.note}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
